import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useCustomQuery } from "@/hooks/useCustomQuery";
import useAlertMessage from "@/hooks/utils/useAlertMessage";
import useButtonLoading from "@/hooks/useButtonLoading";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  EtcCodeSearchType,
  EtcCostInputSchema,
  EtcCostInputType,
  InitEtcCostInput,
  InitEtcCostSearch,
} from "@/types/etcCost.type";
import {
  useDeleteMutation,
  usePostMutation,
  usePutMutation,
} from "@/util/common.fn";
import { ContentTemplate } from "@/components/templates/ContentTemplate";
import EtcCostGrid from "./EtcCostGrid";
import EtcCostInput from "./EtcCostInput";

const EtcCost = () => {
  const [search, setSearch] = useState<EtcCodeSearchType>(InitEtcCostSearch);
  const [selectedRowKey, setSelectedRowKey] = useState<string | null>(null);
  const [isOpen, setOpen] = useState<boolean>(false);

  const gridMain = useRef<any>();

  const { alertErrorMessage, alertSuccessMessage, confirmMessage } =
    useAlertMessage();
  const { setLoading, setUnloading } = useButtonLoading();

  const { data: mainData, refetch: mainRefetch } = useCustomQuery<any[]>(
    `etccost`,
    search
  );

  const { mutateAsync: create } = usePostMutation("etccost");
  const { mutateAsync: update } = usePutMutation("etccost");
  const { mutateAsync: remove } = useDeleteMutation("etccost");

  const searchForm = useForm({ defaultValues: InitEtcCostSearch });
  const inputForm = useForm<EtcCostInputType>({
    defaultValues: InitEtcCostInput,
    resolver: yupResolver(EtcCostInputSchema),
  });

  const { watch, reset, trigger } = inputForm;

  const initPage = () => {
    const mainGridInstance = gridMain.current.instance;
    mainGridInstance.option("focusedRowIndex", -1);
    resetGridChanges();
    setSelectedRowKey(null);
    mainRefetch();
    reset(InitEtcCostInput);
  };

  const resetGridChanges = () => {
    const mainGridInstance = gridMain.current.instance;
    mainGridInstance.option("editing.changes", []);
  };

  const toggleOpen = () => setOpen(!isOpen);

  const onSearch = (data: any) => {
    setSearch(data);

    mainRefetch();
  };

  const onFocusedRowChanged = (e: any) => {
    if (!e.row?.data) return;
    const data = e.row.data;
    setSelectedRowKey(e.row.key);
    reset(data);
  };

  const onClickNewData = () => {
    initPage();
  };

  const onClickSaveData = async () => {
    if (!(await trigger())) return;

    const data = watch();
    const mainGridInstance = gridMain.current.instance;
    const originalKey = selectedRowKey;

    let res: any;

    try {
      setLoading();
      if (selectedRowKey) {
        res = await update({ key: selectedRowKey, data });
      } else {
        res = await create(data);
      }

      if (res.state === 1) {
        initPage();
        await alertSuccessMessage("저장완료");
      }

      mainGridInstance.option("focusedRowKey", originalKey);
    } catch (error) {
      alertErrorMessage(error);
    } finally {
      setUnloading();
    }
  };

  const onClickDeleteData = async () => {
    if (!(await confirmMessage("삭제하시겠습니까?"))) return;
    try {
      setLoading();
      const res: any = await remove({ oid: selectedRowKey });

      if (res.state === 1) {
        initPage();

        await alertSuccessMessage("삭제완료");
      }
    } catch (error) {
      alertErrorMessage(error);
    } finally {
      setUnloading();
    }
  };

  return (
    <ContentTemplate sideWidth={280}>
      <>
        <EtcCostGrid
          gridMain={gridMain}
          mainData={mainData}
          searchForm={searchForm}
          toggleOpen={toggleOpen}
          onSearch={onSearch}
          onFocusedRowChanged={onFocusedRowChanged}
        />
        <EtcCostInput
          selectedRowKey={selectedRowKey}
          inputForm={inputForm}
          toggleOpen={toggleOpen}
          onClickNewData={onClickNewData}
          onClickSaveData={onClickSaveData}
          onClickDeleteData={onClickDeleteData}
        />
      </>
    </ContentTemplate>
  );
};

export default EtcCost;
